import styled from '@emotion/native';
import { Row } from 'components/base';
import { Button, Card } from 'design-system';
import { MARGIN } from 'design-system/theme';
import { useList } from 'hooks/useList';
import { useState } from 'react';

import { type MedicationFetcher, MedicationForm } from './MedicationForm';
import type { MedicationUsageFormData } from './types';

interface FormProps {
  handleSubmit: (data: MedicationUsageFormData[]) => Promise<void>;
  medicationFetcher: MedicationFetcher;
  title?: string;
}

type MedicationFormState = {
  data: Partial<MedicationUsageFormData>;
  hasErrors: boolean;
  hasSubmitted: boolean;
  isDuplicated?: boolean;
};

const ButtonWrapper = styled(Row)({
  marginTop: MARGIN['2xs'],
  width: '100%',
  justifyContent: 'flex-end',
});

export const MedicationListForm = ({
  handleSubmit,
  medicationFetcher,
  title,
}: FormProps) => {
  const list = useList<number, MedicationFormState>([
    {
      key: Math.random(),
      value: {
        data: {},
        hasErrors: true,
        hasSubmitted: false,
        isDuplicated: false,
      },
    },
  ]);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isDuplicate = (
    newData: Partial<MedicationUsageFormData>,
    currentKey: number,
  ) => {
    const newCode = newData.medicationCode;
    if (!newCode) return false;

    return list.items.some(
      (item) =>
        item.key !== currentKey && item.value.data.medicationCode === newCode,
    );
  };

  return (
    <Card unsetZIndex>
      {list.items.map((p, index) => {
        const isSubmittedItem = p.value.hasSubmitted || hasSubmitted;

        return (
          <MedicationForm
            key={p.key}
            title={title}
            onAdd={() => {
              const isDuplicated = isDuplicate(p.value.data, p.key);

              list.update(p.key, {
                ...p.value,
                hasErrors: false,
                hasSubmitted: true,
                isDuplicated,
              });

              if (!isDuplicated) {
                list.add(Math.random(), {
                  data: {},
                  hasErrors: true,
                  hasSubmitted: false,
                  isDuplicated: false,
                });
              }
            }}
            onChange={(data, hasErrors) =>
              list.update(p.key, {
                hasSubmitted: p.value.hasSubmitted,
                data,
                hasErrors,
                isDuplicated: false,
              })
            }
            onRemove={() => {
              list.remove(p.key);
            }}
            canAdd={index === list.items.length - 1}
            canRemove={list.items.length > 1}
            showErrors={isSubmittedItem && p.value.hasErrors}
            initialValues={p.value.data}
            isDuplicated={p.value.isDuplicated}
            medicationFetcher={medicationFetcher}
          />
        );
      })}
      <ButtonWrapper>
        <Button
          variant="contained"
          onPress={() => {
            setHasSubmitted(true);

            const codes = list.items
              .map((item) => item.value.data.medicationCode)
              .filter((code): code is string => !!code);

            const hasDuplicates = new Set(codes).size !== codes.length;

            if (hasDuplicates) {
              const duplicateCodes = codes.filter(
                (code, index, arr) => arr.indexOf(code) !== index,
              );

              list.items.forEach((p) => {
                const code = p.value.data.medicationCode;
                const isDuplicated = code && duplicateCodes.includes(code);
                if (isDuplicated) {
                  list.update(p.key, {
                    ...p.value,
                    hasErrors: false,
                    isDuplicated: true,
                  });
                }
              });

              return;
            }

            const allValid = list.items.every((item) => !item.value.hasErrors);
            if (!allValid) {
              return;
            }

            // casting as the data is known to be valid
            const result = list.items.map(
              (p) => p.value.data as MedicationUsageFormData,
            );
            handleSubmit(result);
          }}
          text={'Submit'}
        />
      </ButtonWrapper>
    </Card>
  );
};
